import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Users, CalendarCheck, DoorOpen, Clock, CheckCircle, XCircle, AlertCircle,
  RefreshCw, FileSpreadsheet, Search, User, Eye, Pencil, X, Undo2, DollarSign,
  TrendingUp, TrendingDown, Minus
} from "lucide-react";
import { useAdmin } from "./AdminContext";
import { MonthDropdown } from "./MonthDropdown";
import { SignatureCell } from "./SignatureCell";
import { FailedCheckinsCard } from "./FailedCheckinsCard";

function StatCard({ icon, label, value, testId }) {
  return (
    <motion.div whileHover={{ scale: 1.02 }} className="stat-card rounded-lg" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <p className="font-mono text-xs uppercase tracking-wider text-vault-gold/80 mb-2">{label}</p>
          <p className="font-outfit text-4xl font-bold text-vault-text">{value}</p>
        </div>
        <div className="text-vault-gold/50">{icon}</div>
      </div>
    </motion.div>
  );
}

// Revenue card — shows the post-tax amount you invoice the railroad (matches
// Billing Summary) with a sub-line for the pre-tax breakdown (matches Profitability).
function RevenueCard({ revenue, testId }) {
  const fmt = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtShort = (n) => {
    const v = Number(n) || 0;
    if (v >= 1000) return `$${(v / 1000).toFixed(1)}k`;
    return `$${v.toFixed(0)}`;
  };
  const total = revenue?.grand_total ?? 0;
  const pretax = revenue?.grand_total_pretax ?? 0;
  const tax = revenue?.railroad_tax ?? 0;
  const rrTotal = revenue?.railroad_total ?? 0;
  const nrTotal = revenue?.non_railroad_total ?? 0;
  const prev = revenue?.prev_period;
  const pct = prev?.pct_change;
  const spark = revenue?.sparkline || [];

  let TrendIcon = Minus;
  let trendColor = "text-vault-text-secondary";
  if (pct !== null && pct !== undefined) {
    if (pct > 0.5) { TrendIcon = TrendingUp; trendColor = "text-green-400"; }
    else if (pct < -0.5) { TrendIcon = TrendingDown; trendColor = "text-red-400"; }
  }
  const pctStr = pct === null || pct === undefined ? null : `${pct > 0 ? '+' : ''}${pct}%`;

  // Build an SVG sparkline path from the last 6 months' grand_total.
  // Viewport: 160x40 (width x height). Last point highlighted with a dot.
  const sparkSvg = (() => {
    if (!spark || spark.length < 2) return null;
    const vals = spark.map(s => Number(s.grand_total) || 0);
    const max = Math.max(...vals, 1);
    const min = Math.min(...vals);
    const range = max - min || 1;
    const w = 160, h = 36;
    const stepX = spark.length === 1 ? 0 : w / (spark.length - 1);
    const points = vals.map((v, i) => {
      const x = i * stepX;
      const y = h - ((v - min) / range) * h;
      return [x, y];
    });
    const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
    const last = points[points.length - 1];
    // Area fill: go down to baseline and back
    const area = `${d} L${w},${h} L0,${h} Z`;
    return (
      <svg width={w} height={h + 4} viewBox={`0 0 ${w} ${h + 4}`} className="overflow-visible" data-testid="revenue-sparkline">
        <path d={area} fill="url(#sparkGrad)" opacity="0.25" />
        <path d={d} fill="none" stroke="currentColor" strokeWidth="1.5" className="text-vault-gold" strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={last[0]} cy={last[1]} r="2.5" className="fill-vault-gold" />
        <defs>
          <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="currentColor" className="text-vault-gold" stopOpacity="0.6" />
            <stop offset="100%" stopColor="currentColor" className="text-vault-gold" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
    );
  })();

  const sparkLabels = spark.length
    ? `${spark[0].label} → ${spark[spark.length - 1].label}`
    : null;

  return (
    <motion.div whileHover={{ scale: 1.02 }} className="stat-card rounded-lg" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className="font-mono text-xs uppercase tracking-wider text-vault-gold/80 mb-2">Revenue (with tax)</p>
          <p className="font-outfit text-3xl font-bold text-vault-text truncate" title={fmt(total)}>{fmt(total)}</p>
          {prev && (
            <div className={`mt-1 flex items-center gap-1 text-xs ${trendColor}`} data-testid="revenue-trend">
              <TrendIcon className="w-3 h-3" />
              <span className="font-medium">{pctStr ?? '—'}</span>
              <span className="text-vault-text-secondary">vs {prev.label}</span>
            </div>
          )}
          {sparkSvg && (
            <div className="mt-2" title={spark.map(s => `${s.label}: ${fmt(s.grand_total)}`).join(' | ')}>
              {sparkSvg}
              <div className="text-[10px] text-vault-text-secondary mt-0.5 flex justify-between">
                <span>{spark[0]?.label ?? ''}</span>
                <span>{spark[spark.length - 1]?.label ?? ''}</span>
              </div>
            </div>
          )}
          <div className="mt-2 space-y-0.5 text-xs text-vault-text-secondary">
            <div>Pre-tax: <span className="text-vault-text font-mono">{fmt(pretax)}</span></div>
            <div>Tax: <span className="text-vault-text font-mono">{fmt(tax)}</span></div>
            <div className="pt-1 border-t border-vault-border/50">
              RR: <span className="text-vault-text font-mono">{fmt(rrTotal)}</span>
              <span className="mx-1">·</span>
              Non-RR: <span className="text-vault-text font-mono">{fmt(nrTotal)}</span>
            </div>
          </div>
        </div>
        <div className="text-vault-gold/50 flex-shrink-0 ml-2"><DollarSign className="w-6 h-6" /></div>
      </div>
    </motion.div>
  );
}

export function DashboardTab() {
  const { API, stats, records, fetchData, syncStatus, setSyncStatus, handleMarkNoBill, markingNoBill, startDate, endDate, setStartDate, setEndDate, isFiltered, handleApplyFilter, handleClearFilter, dateField, handleDateFieldChange } = useAdmin();
  const [roomSearch, setRoomSearch] = useState("");
  const [nameSearch, setNameSearch] = useState("");

  // Dashboard records should be bucketed by the currently-selected dateField.
  // Re-fetch on mount so we don't display records left over from another tab
  // (e.g. SignInTab pins to check_in_date and would overwrite the shared `records`).
  useEffect(() => {
    fetchData(startDate || null, endDate || null, 0, dateField);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [selectedSignature, setSelectedSignature] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);

  const handleUpdateRecord = async () => {
    if (!editingRecord || !editingRecord.id) { toast.error("No record selected"); return; }
    if (editingRecord.employee_number && !/^\d*$/.test(editingRecord.employee_number)) { toast.error("Employee number must contain only numbers"); return; }
    try {
      // Send only non-empty fields so a user clearing a date/time input doesn't
      // accidentally blank out the stored value. Backend's BookingUpdate model
      // already ignores `None`, but stripping empty strings here is the safety net.
      const candidate = {
        employee_number: editingRecord.employee_number,
        employee_name: editingRecord.employee_name,
        room_number: editingRecord.room_number,
        check_in_date: editingRecord.check_in_date,
        check_in_time: editingRecord.check_in_time,
        check_out_date: editingRecord.check_out_date,
        check_out_time: editingRecord.check_out_time,
      };
      const payload = Object.fromEntries(
        Object.entries(candidate).filter(([, v]) => v !== "" && v !== null && v !== undefined)
      );
      await axios.put(`${API}/admin/bookings/${editingRecord.id}`, payload);
      toast.success("Record updated successfully");
      setEditingRecord(null);
      fetchData(startDate || null, endDate || null);
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to update record"); }
  };

  const handleUndoCheckout = async (record) => {
    if (!confirm(`Undo checkout for ${record.employee_name} (Room ${record.room_number})? This will set them back as active/in-house.`)) return;
    try {
      await axios.post(`${API}/admin/records/${record.id}/undo-checkout`);
      toast.success(`${record.employee_name} is back as checked-in in Room ${record.room_number}`);
      fetchData(startDate || null, endDate || null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to undo checkout");
    }
  };

  const searchLower = nameSearch.toLowerCase();
  const filteredRecords = records.filter(record => {
    const roomMatch = !roomSearch || record.room_number?.toString().includes(roomSearch);
    const nameMatch = !nameSearch || record.employee_name?.toLowerCase().includes(searchLower) || record.employee_number?.toString().toLowerCase().includes(searchLower);
    return roomMatch && nameMatch;
  });

  return (
    <>
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="font-outfit text-3xl font-bold text-vault-text tracking-tight">Dashboard</h1>
            <p className="text-vault-text-secondary font-manrope mt-1">Manage guest check-ins and billing</p>
          </div>
        </div>
      </div>

      <div className={`grid grid-cols-1 md:grid-cols-2 ${stats.revenue ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-6 mb-8`}>
        <StatCard icon={<Users className="w-6 h-6" />} label="Total Guests" value={stats.total_guests} testId="stat-total-guests" />
        <StatCard icon={<CalendarCheck className="w-6 h-6" />} label="Total Check-ins" value={stats.total_checkins} testId="stat-total-checkins" />
        <StatCard icon={<DoorOpen className="w-6 h-6" />} label="Active Stays" value={stats.active_stays} testId="stat-active-stays" />
        <StatCard icon={<Clock className="w-6 h-6" />} label="Completed Stays" value={stats.completed_stays} testId="stat-completed-stays" />
        {stats.revenue && <RevenueCard revenue={stats.revenue} testId="stat-revenue" />}
      </div>

      {/* Sync Status Card */}
      <Card className="bg-vault-surface border-vault-border mb-8" data-testid="sync-status-card">
        <CardHeader className="border-b border-vault-border py-3">
          <div className="flex items-center justify-between">
            <CardTitle className="font-outfit text-lg text-vault-text flex items-center gap-2">
              <RefreshCw className={`w-5 h-5 ${syncStatus.running ? 'text-vault-gold animate-spin' : 'text-vault-gold'}`} />Last Sync Results
              {syncStatus.agent_version && <span className="text-xs font-mono text-vault-text-secondary bg-vault-border/50 px-2 py-0.5 rounded">{syncStatus.agent_version}</span>}
            </CardTitle>
            {syncStatus.last_run && <span className="text-vault-text-secondary text-sm font-manrope">{new Date(syncStatus.last_run).toLocaleString()}</span>}
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {syncStatus.last_results ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-3 p-3 bg-green-500/10 rounded-lg border border-green-500/20"><CheckCircle className="w-5 h-5 text-green-500" /><div><p className="text-vault-text-secondary text-xs">Verified</p><p className="text-xl font-bold text-green-400 font-mono" data-testid="sync-verified-count">{syncStatus.last_results.verified?.length || 0}</p></div></div>
              <div className="flex items-center gap-3 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20"><XCircle className="w-5 h-5 text-amber-500" /><div><p className="text-vault-text-secondary text-xs">No Bill</p><p className="text-xl font-bold text-amber-400 font-mono" data-testid="sync-nobill-count">{syncStatus.last_results.no_bill?.length || 0}</p></div></div>
              <div className="flex items-center gap-3 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20"><AlertCircle className="w-5 h-5 text-blue-500" /><div><p className="text-vault-text-secondary text-xs">Missing</p><p className="text-xl font-bold text-blue-400 font-mono" data-testid="sync-missing-count">{syncStatus.last_results.missing_in_hodler?.length || 0}</p></div></div>
              <div className="flex items-center gap-3 p-3 bg-red-500/10 rounded-lg border border-red-500/20"><XCircle className="w-5 h-5 text-red-500" /><div><p className="text-vault-text-secondary text-xs">Errors</p><p className="text-xl font-bold text-red-400 font-mono" data-testid="sync-errors-count">{syncStatus.last_results.errors?.length || 0}</p></div></div>
            </div>
          ) : (
            <div className="text-center py-4 text-vault-text-secondary"><RefreshCw className="w-8 h-8 mx-auto mb-2 opacity-30" /><p>No sync has been run yet</p><p className="text-xs mt-1">Go to Portal Settings to run a sync</p></div>
          )}
          {syncStatus.running && <div className="mt-4 flex items-center gap-2 text-vault-gold"><RefreshCw className="w-4 h-4 animate-spin" /><span className="text-sm">{syncStatus.progress || "Sync in progress..."}</span></div>}
          
          {syncStatus.last_results?.missing_in_hodler?.length > 0 && !syncStatus.running && (
            <div className="mt-4 border-t border-vault-border pt-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-blue-400 font-medium flex items-center gap-2"><AlertCircle className="w-4 h-4" />Missing in Hodler Inn ({syncStatus.last_results.missing_in_hodler.length})</h4>
                <p className="text-xs text-vault-text-secondary">Found on portal but not in your records - need "No Bill"</p>
              </div>
              <div className="max-h-64 overflow-y-auto space-y-2 bg-black/30 rounded-lg p-3">
                {syncStatus.last_results.missing_in_hodler.map((entry, idx) => {
                  const targetDate = entry.date || syncStatus.last_run?.split('T')[0];
                  const entryKey = `${entry.name}-${targetDate}`;
                  return (
                    <div key={idx} className="flex items-center justify-between p-3 bg-blue-900/20 rounded border border-blue-500/20">
                      <div className="flex-1">
                        <p className="text-vault-text font-medium">{entry.name || entry.api_name || 'Unknown'}</p>
                        <p className="text-xs text-vault-text-secondary">{entry.employee_id && `ID: ${entry.employee_id} | `}Date: {targetDate || 'Unknown'}{entry.best_matches?.length > 0 && <span className="text-amber-400 ml-2">Possible: {entry.best_matches.slice(0,2).join(', ')}</span>}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" onClick={() => handleMarkNoBill(entry, targetDate)} disabled={markingNoBill[entryKey]} className="bg-amber-600 hover:bg-amber-700 text-white text-xs" data-testid={`mark-nobill-${idx}`}>{markingNoBill[entryKey] ? "Marking..." : "Mark No Bill"}</Button>
                        <Button size="sm" variant="ghost" onClick={() => { setSyncStatus(prev => ({...prev, last_results: {...prev.last_results, missing_in_hodler: prev.last_results.missing_in_hodler.filter((_, i) => i !== idx)}})); toast.success("Entry removed from list"); }} className="text-red-400 hover:text-red-300 hover:bg-red-900/30 h-8 w-8 p-0" title="Remove from list" data-testid={`remove-missing-${idx}`}><X className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-vault-text-secondary mt-2">Click "Mark No Bill" to update the portal entry.</p>
            </div>
          )}
        </CardContent>
      </Card>

      <FailedCheckinsCard />

      {/* Records Table */}
      <Card className="bg-vault-surface border-vault-border" data-testid="records-table-card">
        <CardHeader className="border-b border-vault-border">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <CardTitle className="font-outfit text-xl text-vault-text flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-vault-gold" />Guest Records</CardTitle>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative"><Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-vault-text-secondary" /><Input type="text" placeholder="Search room #..." value={roomSearch} onChange={(e) => setRoomSearch(e.target.value)} className="pl-8 w-28 sm:w-32 h-8 bg-black/50 border-vault-border text-vault-text text-sm" data-testid="room-search-input" /></div>
                <div className="relative"><User className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-vault-text-secondary" /><Input type="text" placeholder="Search name or ID..." value={nameSearch} onChange={(e) => setNameSearch(e.target.value)} className="pl-8 w-36 sm:w-44 h-8 bg-black/50 border-vault-border text-vault-text text-sm" data-testid="name-search-input" /></div>
                {(roomSearch || nameSearch) && <Button variant="ghost" size="sm" onClick={() => { setRoomSearch(""); setNameSearch(""); }} className="h-8 px-2 text-vault-text-secondary hover:text-vault-text"><X className="w-4 h-4" /></Button>}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-vault-border/50">
              <MonthDropdown dateFieldOverride="check_out_date" />
              <span className="text-vault-text-secondary text-sm ml-2">or custom:</span>
              <DatePicker date={startDate} onDateChange={setStartDate} placeholder="Start Date" className="h-8" />
              <span className="text-vault-text-secondary text-sm">to</span>
              <DatePicker date={endDate} onDateChange={setEndDate} placeholder="End Date" className="h-8" />
              <Button onClick={handleApplyFilter} size="sm" className="vault-btn-primary h-8 px-3" data-testid="records-apply-filter-btn">Apply</Button>
              {isFiltered && <Button onClick={handleClearFilter} variant="ghost" size="sm" className="h-8 px-2 text-vault-text-secondary hover:text-vault-gold" data-testid="records-clear-filter-btn"><X className="w-4 h-4 mr-1" />Clear</Button>}
              {isFiltered && <span className="text-xs text-vault-gold ml-2">Filtered: {startDate || 'any'} to {endDate || 'any'}</span>}
              <div className="flex items-center gap-1 ml-auto bg-black/40 rounded-md p-0.5 border border-vault-border" data-testid="dashboard-date-field-toggle">
                <button
                  onClick={() => handleDateFieldChange('check_out_date')}
                  className={`text-xs px-2 py-1 rounded transition-colors ${dateField === 'check_out_date' ? 'bg-vault-gold text-black font-medium' : 'text-vault-text-secondary hover:text-vault-text'}`}
                  data-testid="date-field-checkout-btn"
                  title="Group by checkout date — matches railroad billing report"
                >
                  By Check-Out
                </button>
                <button
                  onClick={() => handleDateFieldChange('check_in_date')}
                  className={`text-xs px-2 py-1 rounded transition-colors ${dateField === 'check_in_date' ? 'bg-vault-gold text-black font-medium' : 'text-vault-text-secondary hover:text-vault-text'}`}
                  data-testid="date-field-checkin-btn"
                  title="Group by check-in date — includes active stays"
                >
                  By Check-In
                </button>
              </div>
            </div>
            <div className="text-xs text-vault-text-secondary mt-2" data-testid="records-count-label">
              Showing <span className="text-vault-gold font-medium">{filteredRecords.length}</span> of <span className="text-vault-text">{records.length}</span> records
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="h-[400px]">
            <Table>
              <TableHeader>
                <TableRow className="table-header border-vault-border hover:bg-transparent">
                  <TableHead className="text-vault-gold w-12">#</TableHead>
                  <TableHead className="text-vault-gold">Employee ID</TableHead><TableHead className="text-vault-gold">Name</TableHead>
                  <TableHead className="text-vault-gold">Room</TableHead><TableHead className="text-vault-gold">Check-In</TableHead>
                  <TableHead className="text-vault-gold">Check-Out</TableHead><TableHead className="text-vault-gold">Hours</TableHead>
                  <TableHead className="text-vault-gold">Nights</TableHead><TableHead className="text-vault-gold">Status</TableHead>
                  <TableHead className="text-vault-gold">Signature</TableHead><TableHead className="text-vault-gold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRecords.length === 0 ? (
                  <TableRow><TableCell colSpan={11} className="text-center text-vault-text-secondary py-8">{roomSearch || nameSearch ? 'No guests found matching search criteria' : 'No records found'}</TableCell></TableRow>
                ) : filteredRecords.map((record, idx) => (
                  <TableRow key={record.id} className="table-row border-vault-border" data-testid={`record-row-${record.id}`}>
                    <TableCell className="font-mono text-vault-text-secondary text-xs">{idx + 1}</TableCell>
                    <TableCell className="font-mono text-vault-text">{record.employee_number}</TableCell>
                    <TableCell className="text-vault-text">{record.employee_name}</TableCell>
                    <TableCell className="font-mono text-vault-gold">{record.room_number}</TableCell>
                    <TableCell className="text-vault-text-secondary text-sm"><div>{record.check_in_date}</div><div className="text-xs">{record.check_in_time}</div></TableCell>
                    <TableCell className="text-vault-text-secondary text-sm">{record.check_out_date ? <><div>{record.check_out_date}</div><div className="text-xs">{record.check_out_time}</div></> : <span className="text-vault-text-secondary">—</span>}</TableCell>
                    <TableCell className="font-mono text-vault-text">{record.total_hours !== null ? `${record.total_hours}h` : "—"}</TableCell>
                    <TableCell className="font-mono text-vault-gold font-bold">{record.total_nights !== null ? record.total_nights : "—"}</TableCell>
                    <TableCell>{record.is_checked_out ? <span className="flex items-center gap-1 text-vault-success text-sm"><CheckCircle className="w-4 h-4" />Completed</span> : <span className="flex items-center gap-1 text-vault-gold text-sm"><XCircle className="w-4 h-4" />Active</span>}</TableCell>
                    <TableCell>
                      <SignatureCell record={record} API={API} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" className="text-vault-text-secondary hover:text-vault-gold" onClick={() => setEditingRecord({ id: record.id, employee_number: record.employee_number || "", employee_name: record.employee_name || "", room_number: record.room_number || "", check_in_date: record.check_in_date || "", check_in_time: record.check_in_time || "", check_out_date: record.check_out_date || "", check_out_time: record.check_out_time || "" })} data-testid={`edit-record-${record.id}`}><Pencil className="w-4 h-4" /></Button>
                        {record.is_checked_out && (
                          <Button variant="ghost" size="sm" className="text-vault-text-secondary hover:text-amber-400" onClick={() => handleUndoCheckout(record)} title="Undo Checkout" data-testid={`undo-checkout-${record.id}`}><Undo2 className="w-4 h-4" /></Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Edit Record Dialog */}
      <Dialog open={!!editingRecord} onOpenChange={(open) => !open && setEditingRecord(null)}>
        <DialogContent className="bg-vault-surface border-vault-border max-w-lg">
          <DialogHeader><DialogTitle className="font-outfit text-vault-text">Edit Guest Record</DialogTitle></DialogHeader>
          {editingRecord && (
            <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
              <div><label className="text-sm text-vault-text-secondary mb-2 block">Employee Number</label><Input value={editingRecord.employee_number} onChange={(e) => setEditingRecord({...editingRecord, employee_number: e.target.value.replace(/[^0-9]/g, '')})} placeholder="Enter employee number (numbers only)" className="vault-input font-mono" inputMode="numeric" data-testid="edit-record-employee-number" /><p className="text-xs text-vault-text-secondary mt-1">Numbers only</p></div>
              <div><label className="text-sm text-vault-text-secondary mb-2 block">Employee Name</label><Input value={editingRecord.employee_name} onChange={(e) => setEditingRecord({...editingRecord, employee_name: e.target.value})} placeholder="Enter employee name" className="vault-input" data-testid="edit-record-employee-name" /></div>
              <div><label className="text-sm text-vault-text-secondary mb-2 block">Room Number</label><Input value={editingRecord.room_number} onChange={(e) => setEditingRecord({...editingRecord, room_number: e.target.value})} placeholder="Enter room number" className="vault-input font-mono" data-testid="edit-record-room-number" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-vault-text-secondary mb-2 block">Check-In Date</label>
                  <Input type="date" value={editingRecord.check_in_date} onChange={(e) => setEditingRecord({...editingRecord, check_in_date: e.target.value})} className="vault-input font-mono" data-testid="edit-record-check-in-date" />
                </div>
                <div>
                  <label className="text-sm text-vault-text-secondary mb-2 block">Check-In Time</label>
                  <Input type="time" value={editingRecord.check_in_time} onChange={(e) => setEditingRecord({...editingRecord, check_in_time: e.target.value})} className="vault-input font-mono" data-testid="edit-record-check-in-time" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-vault-text-secondary mb-2 block">Check-Out Date</label>
                  <Input type="date" value={editingRecord.check_out_date} onChange={(e) => setEditingRecord({...editingRecord, check_out_date: e.target.value})} className="vault-input font-mono" data-testid="edit-record-check-out-date" />
                </div>
                <div>
                  <label className="text-sm text-vault-text-secondary mb-2 block">Check-Out Time</label>
                  <Input type="time" value={editingRecord.check_out_time} onChange={(e) => setEditingRecord({...editingRecord, check_out_time: e.target.value})} className="vault-input font-mono" data-testid="edit-record-check-out-time" />
                </div>
              </div>
              <p className="text-xs text-vault-text-secondary italic">Leave a field blank to keep its current value. Setting check-out date/time alone does NOT mark the guest checked out — use the dashboard's Check Out button for that.</p>
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild><Button variant="ghost" className="text-vault-text-secondary">Cancel</Button></DialogClose>
            <Button onClick={handleUpdateRecord} className="vault-btn-primary" data-testid="save-record-btn">Update Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
